/*
 * Slider for sections/about-hero.liquid (2 or more slide blocks).
 * Track with translateX, arrows, dots, swipe, autoplay that pauses on hover/focus and with a pause button,
 * no autoplay or glide with prefers-reduced-motion, and the theme editor jumps to the selected block.
 */
if (!customElements.get('about-hero-slider')) {
  class AboutHeroSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.about-hero-slider__track');
      this.slides = Array.from(this.querySelectorAll('.about-hero-slider__slide'));
      this.dots = Array.from(this.querySelectorAll('.about-hero-slider__dot'));
      this.pauseBtn = this.querySelector('.about-hero-slider__pause');
      this.index = 0;
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.autoplay = this.dataset.autoplay === 'true' && !this.reduced;
      this.speed = (parseInt(this.dataset.speed, 10) || 6) * 1000;
      this.paused = false;
      this.hovering = false;

      if (this.reduced) this.classList.add('is-reduced-motion');

      this.querySelector('.about-hero-slider__prev')?.addEventListener('click', () => this.go(this.index - 1, true));
      this.querySelector('.about-hero-slider__next')?.addEventListener('click', () => this.go(this.index + 1, true));
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i, true)));

      if (this.pauseBtn) {
        if (!this.autoplay) this.pauseBtn.hidden = true;
        this.pauseBtn.addEventListener('click', () => {
          this.paused = !this.paused;
          this.pauseBtn.setAttribute('aria-pressed', String(this.paused));
          this.pauseBtn.setAttribute('aria-label', this.paused ? this.pauseBtn.dataset.playLabel : this.pauseBtn.dataset.pauseLabel);
          this.restart();
        });
      }

      this.addEventListener('mouseenter', () => { this.hovering = true; this.restart(); });
      this.addEventListener('mouseleave', () => { this.hovering = false; this.restart(); });
      this.addEventListener('focusin', () => { this.hovering = true; this.restart(); });
      this.addEventListener('focusout', () => { this.hovering = false; this.restart(); });

      // Swipe: horizontal movement over ~40px changes slide, vertical scroll is left alone
      let startX = null;
      let startY = null;
      this.track.addEventListener('touchstart', (e) => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
      }, { passive: true });
      this.track.addEventListener('touchend', (e) => {
        if (startX === null) return;
        const dx = e.changedTouches[0].clientX - startX;
        const dy = e.changedTouches[0].clientY - startY;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) this.go(this.index + (dx < 0 ? 1 : -1), true);
        startX = null;
      });

      // Theme editor: show the block that is selected in the sidebar
      this.onBlockSelect = (e) => {
        const i = this.slides.findIndex((s) => s.contains(e.target) || s === e.target);
        if (i > -1) {
          this.editorHold = true;
          this.go(i, false);
          this.restart();
        }
      };
      this.onBlockDeselect = () => { this.editorHold = false; this.restart(); };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

      this.go(0, false);
      this.restart();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    go(i, manual) {
      const n = this.slides.length;
      this.index = ((i % n) + n) % n;
      this.track.style.transform = `translateX(-${this.index * 100}%)`;
      this.slides.forEach((s, k) => {
        const active = k === this.index;
        s.classList.toggle('is-active', active);
        s.setAttribute('aria-hidden', String(!active));
        s.querySelectorAll('a, button').forEach((el) => { el.tabIndex = active ? 0 : -1; });
      });
      this.dots.forEach((d, k) => d.setAttribute('aria-current', String(k === this.index)));

      // Controls follow the active slide: colour of its text and the side of its text panel
      const slide = this.slides[this.index];
      this.style.setProperty('--slider-fg', slide.dataset.fg || '');
      this.style.setProperty('--slider-bg', slide.dataset.bg || '');
      this.classList.toggle('is-img-left', slide.dataset.imgLeft === 'true');

      if (manual) this.restart();
    }

    restart() {
      clearTimeout(this.timer);
      if (!this.autoplay || this.paused || this.hovering || this.editorHold) return;
      this.timer = setTimeout(() => this.go(this.index + 1, true), this.speed);
    }
  }

  customElements.define('about-hero-slider', AboutHeroSlider);
}
